import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  MenuItem,
  TextField,
  Popover,
  Skeleton,
  Stack,
  Chip,
} from '@mui/material';
import {
  Refresh,
  Check,
  Close as CloseIcon,
  PendingActions,
  EventAvailable,
  BeachAccess,
  ChevronLeft,
  ChevronRight,
  CalendarMonth,
  AccountBalance,
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend, ResponsiveContainer } from 'recharts';
import {
  PageHeader,
  StatCard,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  TableEmptyState,
  ErrorState,
} from '../../components/common/pageKit';
import { formatIndianNumber } from '../../utils/currency';
import { useChartTokens, axisProps, gridProps, tooltipStyle } from '../../dashboard/chartTokens';
import hrTimePayrollService from '../../services/hrTimePayrollService';
import { hrService } from '../../services/hrService';

// ---------------------------------------------------------------------------
// Defensive extraction helpers — the exact field names an aggregation
// endpoint uses can vary; these try the documented/likely name first and
// fall back to reasonable alternates rather than rendering nothing.
// ---------------------------------------------------------------------------

const pick = (obj: any, keys: string[], fallback: any = undefined): any => {
  if (!obj) return fallback;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k];
  }
  return fallback;
};

const isNum = (v: any) => v !== null && v !== undefined && v !== '' && !Number.isNaN(Number(v));
const toNum = (v: any): number | null => (isNum(v) ? Number(v) : null);
const fmtInt = (v: any): string => (isNum(v) ? Number(v).toLocaleString('en-IN') : '—');
const fmtMoneyCompact = (v: any): string => (isNum(v) ? formatIndianNumber(Number(v)) : '—');
const fmtDays = (v: any): string => (isNum(v) ? `${Number(v)}` : '—');

const fmtDate = (v: any): string => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtDateShort = (v: any): string => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

// ---- normalized row shapes pulled out of loosely-typed API rows ----

interface NormalizedLeaveRow {
  id: number | null;
  employeeName: string;
  employeeCode: string;
  leaveType: string;
  fromDate: string | null;
  toDate: string | null;
  days: any;
  reason: string | null;
  appliedOn: string | null;
}

const normalizeLeaveRow = (r: any): NormalizedLeaveRow => ({
  id: pick(r, ['id', 'leaveRequestId', 'leave_request_id'], null),
  employeeName: pick(r, ['employee_name', 'employeeName', 'name'], '—'),
  employeeCode: pick(r, ['employee_code', 'employeeCode'], ''),
  leaveType: pick(r, ['leave_type_name', 'leaveType', 'leave_type', 'type'], '—'),
  fromDate: pick(r, ['from_date', 'fromDate', 'startDate', 'start_date'], null),
  toDate: pick(r, ['to_date', 'toDate', 'endDate', 'end_date'], null),
  days: pick(r, ['days', 'numDays', 'totalDays'], null),
  reason: pick(r, ['reason'], null),
  appliedOn: pick(r, ['created_at', 'createdAt', 'appliedOn', 'applied_on'], null),
});

interface NormalizedLiabilityRow {
  employeeId: number | null;
  employeeName: string;
  employeeCode: string;
  department: string;
  openDays: any;
  estimatedValue: any;
}

const normalizeLiabilityRow = (r: any): NormalizedLiabilityRow => ({
  employeeId: pick(r, ['employee_id', 'employeeId', 'id'], null),
  employeeName: pick(r, ['employee_name', 'employeeName', 'name'], '—'),
  employeeCode: pick(r, ['employee_code', 'employeeCode'], ''),
  department: pick(r, ['department', 'dept_name', 'departmentName'], '—'),
  openDays: pick(r, ['openDays', 'open_days', 'days'], null),
  estimatedValue: pick(r, ['estimatedValue', 'estimated_value', 'value'], null),
});

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
    {children}
  </Typography>
);

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const weekdayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface CalendarDayInfo {
  count: number;
  people: { name: string; leaveType: string }[];
}

const normalizeCalendarPayload = (payload: any): Map<string, CalendarDayInfo> => {
  const map = new Map<string, CalendarDayInfo>();
  const rows: any[] = pick(payload, ['days', 'calendar', 'dates'], []) || [];
  rows.forEach((row) => {
    const rawDate = pick(row, ['date', 'leave_date', 'day', 'calendarDate'], null);
    if (!rawDate) return;
    const key = String(rawDate).slice(0, 10);
    const peopleRaw: any[] = pick(row, ['employees', 'people', 'leaves', 'records'], []) || [];
    const people = peopleRaw.map((p) => ({
      name: pick(p, ['employee_name', 'name', 'employeeName'], '—'),
      leaveType: pick(p, ['leave_type_name', 'leaveType', 'leave_type', 'type'], '—'),
    }));
    const count = toNum(pick(row, ['count', 'total', 'leaveCount', 'onLeaveCount'], null)) ?? people.length;
    map.set(key, { count, people });
  });
  return map;
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface ConfirmState {
  row: NormalizedLeaveRow;
  action: 'APPROVED' | 'REJECTED';
}

const LeaveControlCenter: React.FC = () => {
  const T = useChartTokens();

  const [dashboard, setDashboard] = useState<any>(null);
  const [liability, setLiability] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const now = new Date();
  const [calMonth, setCalMonth] = useState(now.getMonth() + 1);
  const [calYear, setCalYear] = useState(now.getFullYear());
  const [calendarMap, setCalendarMap] = useState<Map<string, CalendarDayInfo>>(new Map());
  const [calendarLoading, setCalendarLoading] = useState(true);
  const [calendarError, setCalendarError] = useState<string | null>(null);

  const [dayAnchor, setDayAnchor] = useState<{ el: HTMLElement; dateKey: string } | null>(null);

  const loadCore = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([hrTimePayrollService.leaveDashboard(), hrTimePayrollService.leaveLiability()])
      .then(([dash, liab]) => {
        setDashboard(dash);
        setLiability(liab);
      })
      .catch((err: any) => setError(err.message || 'Failed to load the leave control center'))
      .finally(() => setLoading(false));
  }, []);

  const loadCalendar = useCallback(() => {
    setCalendarLoading(true);
    setCalendarError(null);
    hrTimePayrollService
      .leaveCalendar({ month: calMonth, year: calYear })
      .then((res: any) => setCalendarMap(normalizeCalendarPayload(res)))
      .catch((err: any) => setCalendarError(err.message || 'Failed to load the leave calendar'))
      .finally(() => setCalendarLoading(false));
  }, [calMonth, calYear]);

  useEffect(() => {
    loadCore();
  }, [loadCore]);

  useEffect(() => {
    loadCalendar();
  }, [loadCalendar]);

  const refreshAll = () => {
    loadCore();
    loadCalendar();
  };

  const counts = dashboard?.counts || {};
  const pendingRows: NormalizedLeaveRow[] = useMemo(
    () => (dashboard?.pending || []).map(normalizeLeaveRow),
    [dashboard]
  );
  const upcomingRows: NormalizedLeaveRow[] = useMemo(
    () => (dashboard?.upcoming || []).map(normalizeLeaveRow),
    [dashboard]
  );
  const utilizationRows: any[] = dashboard?.utilization || [];

  const liabilityRows: NormalizedLiabilityRow[] = useMemo(
    () => (liability?.byEmployee || []).map(normalizeLiabilityRow).slice(0, 50),
    [liability]
  );

  const pendingCount = pick(counts, ['pending', 'pendingCount', 'pendingApprovals'], null);
  const approvedThisMonth = pick(counts, ['approvedThisMonth', 'approved_this_month', 'approvedMonth', 'approved'], null);
  const onLeaveToday = pick(counts, ['onLeaveToday', 'on_leave_today'], null);
  const totalOpenDays = pick(liability?.totals, ['totalOpenDays', 'total_open_days'], null);
  const estimatedValue = pick(liability?.totals, ['estimatedValue', 'estimated_value'], null);

  const handleConfirmAction = async () => {
    if (!confirm || confirm.row.id === null) return;
    setActionBusy(true);
    setActionError(null);
    try {
      await hrService.updateLeaveRequestStatus(confirm.row.id, confirm.action);
      setConfirm(null);
      refreshAll();
    } catch (err: any) {
      setActionError(err.message || 'Failed to update this leave request');
    } finally {
      setActionBusy(false);
    }
  };

  // ---- utilization chart key inference ----
  const utilKeys = useMemo(() => {
    if (!utilizationRows.length) return { labelKey: null as string | null, valueKeys: [] as string[] };
    const sample = utilizationRows[0];
    const keys = Object.keys(sample);
    const labelKey = keys.find((k) => typeof sample[k] === 'string') || keys[0] || null;
    const preferred = ['entitled', 'taken'].filter((k) => isNum(sample[k]));
    const valueKeys = preferred.length
      ? preferred
      : keys.filter((k) => k !== labelKey && isNum(sample[k])).slice(0, 2);
    return { labelKey, valueKeys };
  }, [utilizationRows]);

  // ---- month calendar grid ----
  const daysInMonth = new Date(calYear, calMonth, 0).getDate();
  const firstWeekday = new Date(calYear, calMonth - 1, 1).getDay();
  const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  const calendarCells = useMemo(() => {
    const cells: (number | null)[] = [];
    for (let i = 0; i < firstWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [firstWeekday, daysInMonth]);

  const goPrevMonth = () => {
    if (calMonth === 1) {
      setCalMonth(12);
      setCalYear((y) => y - 1);
    } else {
      setCalMonth((m) => m - 1);
    }
  };
  const goNextMonth = () => {
    if (calMonth === 12) {
      setCalMonth(1);
      setCalYear((y) => y + 1);
    } else {
      setCalMonth((m) => m + 1);
    }
  };

  const openDayPopover = (e: React.MouseEvent<HTMLElement>, dateKey: string) => setDayAnchor({ el: e.currentTarget, dateKey });
  const closeDayPopover = () => setDayAnchor(null);
  const dayAnchorInfo = dayAnchor ? calendarMap.get(dayAnchor.dateKey) : null;

  if (loading && !dashboard) {
    return (
      <Box>
        <Skeleton variant="text" width={280} height={44} />
        <Grid container spacing={2} sx={{ mt: 1, mb: 3 }}>
          {[0, 1, 2, 3, 4].map((i) => (
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }} key={i}>
              <Skeleton variant="rounded" height={92} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rounded" height={260} />
      </Box>
    );
  }

  if (error && !dashboard) {
    return (
      <Box>
        <PageHeader title="Leave Control Center" subtitle="Approvals, calendar, utilization and liability" />
        <ErrorState message={error} onRetry={loadCore} />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Leave Control Center"
        subtitle="Pending approvals, the leave calendar, utilization and open-days liability"
        actions={
          <Tooltip title="Refresh">
            <IconButton onClick={refreshAll} disabled={loading || calendarLoading}>
              <Refresh />
            </IconButton>
          </Tooltip>
        }
      />

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {/* KPI band */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <StatCard title="Pending" value={isNum(pendingCount) ? fmtInt(pendingCount) : null} icon={<PendingActions />} color="#d97706" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <StatCard title="Approved (This Month)" value={isNum(approvedThisMonth) ? fmtInt(approvedThisMonth) : null} icon={<EventAvailable />} color="#059669" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <StatCard title="On Leave Today" value={isNum(onLeaveToday) ? fmtInt(onLeaveToday) : null} icon={<BeachAccess />} color="#0891b2" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <StatCard title="Open Leave Days" value={isNum(totalOpenDays) ? fmtInt(totalOpenDays) : null} icon={<CalendarMonth />} color="#7c3aed" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <StatCard title="Leave Liability" value={fmtMoneyCompact(estimatedValue)} icon={<AccountBalance />} color="#dc2626" />
        </Grid>
      </Grid>

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {/* Pending approvals queue */}
        <Grid size={{ xs: 12, lg: 7 }}>
          <Box>
            <SectionTitle>Pending Approvals</SectionTitle>
            <TableSurface>
              <TableContainer sx={{ overflowX: 'auto', maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {['Employee', 'Type', 'From', 'To', 'Days', 'Reason', 'Applied On', ''].map((h, i) => (
                        <TableCell key={h || i} align={h === 'Days' ? 'right' : 'left'} sx={headerCellSx}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} sx={{ border: 0 }}>
                          <TableEmptyState title="No pending leave requests" hint="Every request has been actioned." />
                        </TableCell>
                      </TableRow>
                    ) : (
                      pendingRows.map((r, i) => (
                        <TableRow key={r.id ?? i} sx={bodyRowSx}>
                          <TableCell sx={{ py: 1.25 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                              {r.employeeName}
                            </Typography>
                            {r.employeeCode && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                {r.employeeCode}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{r.leaveType}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{fmtDate(r.fromDate)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{fmtDate(r.toDate)}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                              {fmtDays(r.days)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 180 }}>
                            <Typography variant="body2" color="text.secondary" noWrap title={r.reason || undefined}>
                              {r.reason || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{fmtDate(r.appliedOn)}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              <Tooltip title="Approve">
                                <span>
                                  <IconButton size="small" color="success" disabled={r.id === null} onClick={() => setConfirm({ row: r, action: 'APPROVED' })}>
                                    <Check fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Reject">
                                <span>
                                  <IconButton size="small" color="error" disabled={r.id === null} onClick={() => setConfirm({ row: r, action: 'REJECTED' })}>
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </TableSurface>
          </Box>
        </Grid>

        {/* Upcoming leaves */}
        <Grid size={{ xs: 12, lg: 5 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <SectionTitle>Upcoming Leaves</SectionTitle>
            {upcomingRows.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No upcoming leaves scheduled.
              </Typography>
            ) : (
              <Stack spacing={1} sx={{ maxHeight: 360, overflowY: 'auto' }}>
                {upcomingRows.map((r, i) => (
                  <Box
                    key={r.id ?? i}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, p: 1.25, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                        {r.employeeName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {r.leaveType} · {fmtDateShort(r.fromDate)} → {fmtDateShort(r.toDate)}
                      </Typography>
                    </Box>
                    <Chip size="small" label={`${fmtDays(r.days)}d`} sx={{ flexShrink: 0 }} />
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {/* Leave calendar */}
        <Grid size={{ xs: 12, lg: 7 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
              <SectionTitle>Leave Calendar</SectionTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton size="small" onClick={goPrevMonth}>
                  <ChevronLeft fontSize="small" />
                </IconButton>
                <TextField select size="small" value={calMonth} onChange={(e) => setCalMonth(Number(e.target.value))} sx={{ minWidth: 130 }}>
                  {monthNames.map((m, i) => (
                    <MenuItem key={m} value={i + 1}>
                      {m}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  type="number"
                  value={calYear}
                  onChange={(e) => setCalYear(Number(e.target.value) || calYear)}
                  sx={{ width: 100 }}
                />
                <IconButton size="small" onClick={goNextMonth}>
                  <ChevronRight fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {calendarError && (
              <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setCalendarError(null)}>
                {calendarError}
              </Alert>
            )}

            <Box sx={{ overflowX: 'auto' }}>
              <Box sx={{ minWidth: 490 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.75, mb: 0.75 }}>
                  {weekdayLabels.map((w, i) => (
                    <Typography
                      key={w}
                      variant="caption"
                      align="center"
                      sx={{ fontWeight: 700, color: i === 0 || i === 6 ? 'text.disabled' : 'text.secondary' }}
                    >
                      {w}
                    </Typography>
                  ))}
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.75 }}>
                  {calendarCells.map((d, i) => {
                    if (d === null) return <Box key={`empty-${i}`} sx={{ minHeight: 62 }} />;
                    const weekday = i % 7;
                    const isWeekend = weekday === 0 || weekday === 6;
                    const dateKey = `${calYear}-${String(calMonth).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                    const info = calendarMap.get(dateKey);
                    const isToday = dateKey === todayKey;
                    return (
                      <Box
                        key={dateKey}
                        onClick={info && info.count > 0 ? (e) => openDayPopover(e, dateKey) : undefined}
                        sx={{
                          minHeight: 62,
                          borderRadius: 1.5,
                          border: '1px solid',
                          borderColor: isToday ? 'primary.main' : 'divider',
                          bgcolor: isWeekend ? 'action.hover' : 'transparent',
                          p: 0.75,
                          cursor: info && info.count > 0 ? 'pointer' : 'default',
                          position: 'relative',
                          '&:hover': info && info.count > 0 ? { borderColor: 'primary.main' } : undefined,
                        }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: isToday ? 800 : 500, color: isWeekend ? 'text.disabled' : 'text.primary' }}>
                          {d}
                        </Typography>
                        {info && info.count > 0 && (
                          <Chip
                            size="small"
                            label={info.count}
                            color="warning"
                            sx={{ position: 'absolute', bottom: 4, right: 4, height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Utilization chart */}
        <Grid size={{ xs: 12, lg: 5 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <SectionTitle>Leave Utilization</SectionTitle>
            {!utilizationRows.length || !utilKeys.labelKey || !utilKeys.valueKeys.length ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No utilization data available.
              </Typography>
            ) : (
              <Box sx={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={utilizationRows} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid {...gridProps(T)} />
                    <XAxis dataKey={utilKeys.labelKey} {...axisProps(T)} minTickGap={12} />
                    <YAxis {...axisProps(T)} width={44} allowDecimals={false} />
                    <RTooltip {...tooltipStyle(T)} />
                    <Legend wrapperStyle={{ fontSize: 11, color: T.textSecondary }} />
                    {utilKeys.valueKeys.map((vk, i) => (
                      <Bar key={vk} dataKey={vk} name={vk} fill={T.series(i)} radius={[3, 3, 0, 0]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Leave liability by employee */}
      <Box sx={{ mb: 1 }}>
        <SectionTitle>Leave Liability by Employee (Top 50)</SectionTitle>
        <TableSurface>
          <TableContainer sx={{ overflowX: 'auto', maxHeight: 440 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['Employee', 'Department', 'Open Days', 'Estimated Value'].map((h, i) => (
                    <TableCell key={h} align={i >= 2 ? 'right' : 'left'} sx={headerCellSx}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {liabilityRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ border: 0 }}>
                      <TableEmptyState title="No leave liability data available" />
                    </TableCell>
                  </TableRow>
                ) : (
                  liabilityRows.map((r, i) => (
                    <TableRow key={r.employeeId ?? i} sx={bodyRowSx}>
                      <TableCell sx={{ py: 1.25 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                          {r.employeeName}
                        </Typography>
                        {r.employeeCode && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                            {r.employeeCode}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 160 }}>
                        <Typography variant="body2" noWrap title={r.department}>
                          {r.department}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtDays(r.openDays)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmtMoneyCompact(r.estimatedValue)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TableSurface>
      </Box>

      {/* Day detail popover */}
      <Popover
        open={!!dayAnchor}
        anchorEl={dayAnchor?.el}
        onClose={closeDayPopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box sx={{ p: 1.5, minWidth: 220, maxWidth: 300 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.75 }}>
            {dayAnchor?.dateKey ? fmtDate(dayAnchor.dateKey) : ''}
          </Typography>
          {dayAnchorInfo && dayAnchorInfo.people.length > 0 ? (
            <Stack spacing={0.5}>
              {dayAnchorInfo.people.map((p, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <Typography variant="body2" noWrap>
                    {p.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {p.leaveType}
                  </Typography>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography variant="caption" color="text.secondary">
              {dayAnchorInfo ? `${dayAnchorInfo.count} on leave` : 'No details available.'}
            </Typography>
          )}
        </Box>
      </Popover>

      {/* Approve / reject confirmation */}
      <Dialog open={confirm !== null} onClose={() => (actionBusy ? undefined : setConfirm(null))}>
        <DialogTitle>{confirm?.action === 'APPROVED' ? 'Approve Leave Request' : 'Reject Leave Request'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm?.action === 'APPROVED' ? 'Approve' : 'Reject'} the {confirm ? fmtDays(confirm.row.days) : ''}-day{' '}
            {confirm?.row.leaveType} request from <strong>{confirm?.row.employeeName}</strong> (
            {confirm ? fmtDate(confirm.row.fromDate) : ''} – {confirm ? fmtDate(confirm.row.toDate) : ''})?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={actionBusy}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            color={confirm?.action === 'APPROVED' ? 'success' : 'error'}
            variant="contained"
            disabled={actionBusy}
          >
            {actionBusy ? 'Working…' : confirm?.action === 'APPROVED' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeaveControlCenter;
